import { z } from 'zod';
import {
  MEASUREMENT_MODES,
  type MeasurementMode,
} from '@obraplan/core/domain/planning/team-measurement';
import { businessDateSchema, idSchema } from './common';
import {
  justificationSchema,
  quarterHoursSchema,
  verificationsSchema,
  type NonConformityRef,
} from './planning';

/**
 * Os contratos de medir por equipe (Fase 30, etapa 1 — RN-405 a RN-409).
 *
 * Dois pedidos: ler as frentes que cada equipe tem para medir no dia, e gravar a medição de uma
 * equipe numa frente × serviço, com a checklist no mesmo envelope — como a medição da pessoa
 * (RN-218): a quantidade só grava se a verificação passar.
 */

export const measurementModeSchema = z.enum(
  MEASUREMENT_MODES as unknown as [MeasurementMode, ...MeasurementMode[]],
);

export const getDayTeamMeasurementsSchema = z.object({
  projectId: idSchema,
  date: businessDateSchema,
});
export type GetDayTeamMeasurementsInput = z.infer<typeof getDayTeamMeasurementsSchema>;

export const recordTeamMeasurementSchema = z.object({
  projectId: idSchema,
  date: businessDateSchema,
  teamId: idSchema,
  activityId: idSchema,
  serviceId: idSchema,
  /** `null` tira a medição do dia; número grava (ou troca). */
  quantity: z
    .number()
    .finite('Quantidade inválida.')
    .nonnegative('A quantidade não pode ser negativa.')
    .max(1_000_000_000, 'Quantidade alta demais.')
    .nullable(),
  verifications: verificationsSchema,
});
export type RecordTeamMeasurementInput = z.infer<typeof recordTeamMeasurementSchema>;

/** Uma pessoa da equipe naquela frente × serviço, com as horas alocadas no dia. */
export interface TeamFrontMemberView {
  readonly resourceId: string;
  readonly name: string;
  readonly quarterHours: number;
}

/** Uma frente × serviço de uma equipe no dia, e o que já foi medido nela (RN-406). */
export interface TeamFrontView {
  readonly activityId: string;
  /** "ALVENARIA — 3º Pavimento", o rótulo de sempre da frente. */
  readonly activityLabel: string;
  readonly towerName: string;
  readonly floorName: string;
  readonly disciplineName: string;
  readonly serviceId: string;
  readonly serviceName: string;
  /** A unidade do serviço (RN-232). */
  readonly unit?: { readonly name: string; readonly code: string; readonly decimals: number };
  readonly members: readonly TeamFrontMemberView[];
  readonly quarterHours: number;
  /** A meta da equipe: a soma das metas de quem foi alocado. */
  readonly plannedQuantity?: number;
  /** Ausente = ainda não medida. */
  readonly measurement?: {
    readonly id: string;
    readonly quantity: number;
    readonly measuredAt: string;
    readonly measuredByName?: string;
  };
}

export interface TeamOfDayView {
  readonly teamId: string;
  readonly teamName: string;
  readonly leaderName?: string;
  readonly fronts: readonly TeamFrontView[];
}

/** O dia da obra por equipe: o modo, e as equipes com as frentes a medir. */
export interface DayTeamMeasurementsView {
  readonly date: string;
  readonly mode: MeasurementMode;
  /** Vazio na obra por pessoa: lá não há medição de equipe. */
  readonly teams: readonly TeamOfDayView[];
}

export interface RecordTeamMeasurementResult {
  /** `null` quando a medição foi tirada. */
  readonly measurementId: string | null;
  readonly nonConformities: number;
  /** As NCs desta medição, por item — para a tela subir as fotos do modal (Fase 37, RN-479). */
  readonly nonConformityRefs: readonly NonConformityRef[];
}

/**
 * O "Lançar trabalho" da equipe (Fase 30, etapa 2 — D6, RN-421).
 *
 * Quem ninguém planejou e trabalhou em equipe: escolhe-se a equipe, marca-se quem trabalhou com
 * as horas de cada um, e informa-se **uma** quantidade — a da equipe. Tudo numa transação: a
 * alocação e a conferência de cada pessoa, e a medição da equipe com a checklist. Uma recusa
 * qualquer (a jornada de alguém, a frente já planejada para outro) desfaz o lançamento inteiro.
 */
export const recordReportedTeamWorkSchema = z
  .object({
    projectId: idSchema,
    date: businessDateSchema,
    teamId: idSchema,
    activityId: idSchema,
    serviceId: idSchema,
    members: z
      .array(z.object({ resourceId: idSchema, quarterHours: quarterHoursSchema }))
      .min(1, 'Marque quem da equipe trabalhou.')
      .max(80, 'Gente demais num lançamento só.'),
    /** Ausente: lança só as horas, e a equipe é medida depois. */
    quantity: z
      .number()
      .finite('Quantidade inválida.')
      .nonnegative('A quantidade não pode ser negativa.')
      .max(1_000_000_000, 'Quantidade alta demais.')
      .optional(),
    verifications: verificationsSchema,
    /** O motivo da hora extra (RN-045), para quem passar da jornada do dia. */
    justification: justificationSchema.optional(),
  })
  .refine(
    (value) =>
      new Set(value.members.map((member) => member.resourceId)).size === value.members.length,
    { message: 'A mesma pessoa aparece duas vezes no lançamento.', path: ['members'] },
  );
export type RecordReportedTeamWorkInput = z.infer<typeof recordReportedTeamWorkSchema>;

export interface ReportedTeamWorkResult {
  /** Uma alocação por pessoa lançada. */
  readonly allocations: number;
  /** A medição da equipe, quando veio quantidade. */
  readonly measurementId?: string;
  readonly nonConformities: number;
  /** As NCs do lançamento, por item — para a tela subir as fotos do modal (Fase 37, RN-479). */
  readonly nonConformityRefs: readonly NonConformityRef[];
}
